/**
 * The 1.02 patch panel.
 *
 * A disc install is Fallout 2 v1.0, and v1.0 ships with bugs that can end a
 * playthrough rather than merely annoy it. So the CD path is not finished when
 * the files land in the vault — the player has to be told, in the same breath,
 * what they have and how to fix it.
 *
 * ## Why this only ever links
 *
 * `patch000.dat` is Interplay's, not ours. This app's whole premise is that it
 * ships no game content, and a patch is game content. We do not host it, mirror
 * it, bundle it, or proxy a download through the page — the panel offers links
 * to the places that have carried the official US 1.02d patch for two decades,
 * the player fetches it themselves, and drops the file back in. That also means
 * the bytes the engine loads are bytes the player chose, which is the only
 * version of this that is honest.
 *
 * ## What we do *not* claim about GOG and Steam
 *
 * "The store copies are already patched" is the obvious thing to write here and
 * it is not supportable. The digital re-releases are built from the *European*
 * line, whose equivalent is 1.02e, not the US 1.02d this panel links to; and
 * whether a given store build actually carries a `patch000.dat` varies by store
 * and platform. The local reference install used to develop this shell is a
 * case in point — it has no `patch000.dat` at all. So the panel tells the player
 * to look in their install folder rather than asserting what is in it.
 */

import { PatchError, installPatch } from '../import';
import { $, bytes, note, toast } from './dom';

export interface PatchMirror {
  label: string;
  href: string;
  blurb: string;
}

/**
 * Every URL here was fetched and confirmed to return a real page listing the
 * official 1.02d patch before it shipped. They are *landing* pages rather than
 * direct file links on purpose: a description page survives a mirror
 * reorganising its file store, and it lets the player see what they are getting.
 *
 * The list is shorter than it wants to be, and deliberately so. No Mutants
 * Allowed and ModDB both carry this patch and both were checked — both answer
 * 403 to anything that is not a residential browser, so neither could be
 * confirmed from here. Shipping a link we could not resolve is how a download
 * page rots into a dead end, so they are named in the UI as places to search
 * rather than linked as places we vouch for.
 */
export const PATCH_MIRRORS: PatchMirror[] = [
  {
    label: 'The Patches Scrolls',
    href: 'https://www.patches-scrolls.com/fallout2.php',
    blurb: 'Full Fallout 2 patch index. You want "english/US patch 1.02d for PC".',
  },
  {
    label: 'The Patches Scrolls (mirror)',
    href: 'https://www.patches-scrolls.de/patch/1595/7/43314/download',
    blurb: 'The same archive on its German domain, straight to the 1.02d entry.',
  },
  {
    label: 'Fallout Génération',
    href: 'https://fallout-generation.com/files/file/63-fallout2-patch-officiel-102d-us/',
    blurb: 'French community archive carrying the US 1.02d patch. Needs a free account.',
  },
];

export interface PatchPanel {
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

function link(m: PatchMirror): HTMLElement {
  const row = document.createElement('div');
  row.className = 'mirror';
  const a = document.createElement('a');
  a.href = m.href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.textContent = m.label;
  const span = document.createElement('span');
  span.textContent = m.blurb;
  row.append(a, span);
  return row;
}

/**
 * @param onInstalled fires after the vault gains a patch000.dat, so the caller
 *   can refresh the gate summary and drop the warning badge.
 * @param onContinue fires when the player chooses to play v1.0 anyway.
 */
export function mountPatchPanel(onInstalled: () => void, onContinue: () => void): PatchPanel {
  const overlay = $('#overlay-patch');
  const messages = $('#patch-messages');
  const mirrors = $('#patch-mirrors');
  const input = $<HTMLInputElement>('#input-patch');
  const dz = $('#patch-dropzone');
  let busy = false;

  mirrors.replaceChildren(...PATCH_MIRRORS.map(link));

  const accept = async (file: File) => {
    if (busy) return;
    busy = true;
    messages.replaceChildren();
    try {
      const result = await installPatch(file);
      note(
        messages,
        `Added patch000.dat — ${bytes(result.info.totalSize)}, ${result.info.fileCount} files inside` +
          (result.extractedFrom ? ` (extracted from ${result.extractedFrom})` : '') +
          '. You are now on 1.02.',
        'ok'
      );
      toast('Patch 1.02 installed.', 'ok');
      onInstalled();
    } catch (e) {
      const msg = e instanceof PatchError ? e.message : e instanceof Error ? e.message : String(e);
      note(messages, msg, 'bad');
      console.error('[patch]', e);
    } finally {
      busy = false;
    }
  };

  $('#btn-patch-pick').addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    if (f) void accept(f);
    input.value = '';
  });

  const hot = (on: boolean) => dz.classList.toggle('hot', on);
  for (const type of ['dragenter', 'dragover'] as const) {
    dz.addEventListener(type, (e) => {
      e.preventDefault();
      hot(true);
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) dz.addEventListener(type, () => hot(false));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    hot(false);
    const f = (e as DragEvent).dataTransfer?.files?.[0];
    if (f) void accept(f);
  });

  const panel: PatchPanel = {
    open() {
      messages.replaceChildren();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
    },
    get isOpen() {
      return overlay.classList.contains('open');
    },
  };

  $('#btn-patch-close').addEventListener('click', () => panel.close());
  $('#btn-patch-continue').addEventListener('click', () => {
    panel.close();
    onContinue();
  });

  return panel;
}
